/**
 * Sample Data Banner
 *
 * A dismissible bottom banner that allows users to delete sample data.
 * Shows for max 48 hours after sample data was seeded.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { X, Trash2, Loader2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useSampleDataBanner } from '@/hooks/useSampleDataBanner';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

export function SampleDataBanner() {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const { isVisible, isDeleting, dismissBanner, deleteAllSampleData } = useSampleDataBanner();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);

  // Don't render if not visible
  if (!isVisible) return null;

  const handleDismiss = async () => {
    setIsDismissing(true);
    try {
      await dismissBanner();
    } catch {
      toast.error(t('sampleData.banner.dismissError', 'Could not dismiss banner'));
    } finally {
      setIsDismissing(false);
    }
  };

  const handleDelete = async () => {
    setShowConfirmDialog(false);
    const success = await deleteAllSampleData();

    if (success) {
      toast.success(t('sampleData.banner.deleteSuccess', 'Sample data deleted successfully'));
      // Invalidate all entity caches to refresh the current page
      await queryClient.invalidateQueries({ queryKey: ['customers'] });
      await queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      await queryClient.invalidateQueries({ queryKey: ['purchaseInvoices'] });
      await queryClient.invalidateQueries({ queryKey: ['salesInvoices'] });
      await queryClient.invalidateQueries({ queryKey: ['appointments'] });
      await queryClient.invalidateQueries({ queryKey: ['partStockLines'] });
      await queryClient.invalidateQueries({ queryKey: ['workOrders'] });
    } else {
      toast.error(
        t('sampleData.banner.deleteError', 'Failed to delete sample data. Please try again.')
      );
    }
  };

  return (
    <>
      {/* Banner */}
      <div
        className={cn(
          'fixed bottom-0 left-0 right-0 z-40',
          'bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10',
          'border-t border-primary/20',
          'backdrop-blur-sm',
          'px-4 py-3',
          'animate-in slide-in-from-bottom duration-300'
        )}
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          {/* Left: Icon + Text */}
          <div className="flex items-center gap-3 min-w-0">
            <div className="shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-primary" />
            </div>
            <p className="text-sm text-foreground truncate">
              {t('sampleData.banner.message', 'Sample data has been added. Want to start clean?')}
            </p>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="default"
              size="sm"
              onClick={() => setShowConfirmDialog(true)}
              disabled={isDeleting}
              className="gap-2"
            >
              {isDeleting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
              <span className="hidden sm:inline">
                {t('sampleData.banner.deleteButton', 'Delete sample data')}
              </span>
              <span className="sm:hidden">
                {t('sampleData.banner.deleteButtonShort', 'Delete')}
              </span>
            </Button>

            <Button
              variant="ghost"
              size="icon"
              onClick={handleDismiss}
              disabled={isDismissing}
              className="h-8 w-8 text-muted-foreground hover:text-foreground"
              aria-label={t('sampleData.banner.dismiss', 'Dismiss')}
            >
              {isDismissing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <X className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('sampleData.dialog.title', 'Delete sample data?')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                'sampleData.dialog.description',
                'This will remove all demo vehicles, customers, invoices, inventory and work orders created as sample data. This cannot be undone.'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('sampleData.dialog.confirm', 'Delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
